import type { ProgramProgressBgColorByField, ProgramProgressCell } from '../types/domain';
import {
  formatClassRecordCodeForDisplay,
  formatEvaluationCodeForDisplay,
  formatLearningTextForDisplay,
  getCrossTopicAndNoteForDisplay,
  getUnitTextAndNoteForDisplay,
  composeEvaluationMemoDisplayNote,
  parseMemoFieldStorageDeep,
  parseProgramProgressMemoJsonForDisplay,
} from './programCellDisplayText';
import { cellLearningTextForEditor } from './programLearningContentFields';

/**
 * 연간진도표 `AnnualProgressPage` — `mapProgressLikeToDisplayFields`와 동일
 * (단원/학습/메모/평가/수업기록 표시용 파싱)
 */
export type ProgramProgressLikeRow = Pick<
  ProgramProgressCell,
  | 'unit_text'
  | 'learning_text'
  | 'page_text'
  | 'assistant_page_text'
  | 'current_period_text'
  | 'total_period_text'
  | 'material_text'
  | 'record_cross_curricular_text'
  | 'record_memo_text'
  | 'record_evaluation_text'
  | 'record_class_text'
  | 'record_member_group_id'
> &
  Partial<Pick<ProgramProgressCell, 'bg_color_by_field'>>;

function classRecordKindFromRaw(recordClassText: string): 'empty' | 'lesson' | 'eval_log' | 'custom' {
  const { text } = parseMemoFieldStorageDeep(recordClassText ?? '');
  const code = text.trim();
  if (code === 'lesson') return 'lesson';
  if (code === 'eval_log') return 'eval_log';
  if (code) return 'custom';
  return 'empty';
}

function parseClassRecordBehaviorNote(raw: string): string {
  let text = (raw ?? '').trim();
  if (!text) return '';
  let behaviorNote = '';
  for (let i = 0; i < 5; i += 1) {
    const candidate = text.trim();
    if (!candidate.startsWith('{')) break;
    try {
      const parsed = JSON.parse(candidate) as { text?: unknown; behavior_note?: unknown };
      if (!behaviorNote && typeof parsed.behavior_note === 'string') behaviorNote = parsed.behavior_note.trim();
      text = typeof parsed.text === 'string' ? parsed.text : '';
    } catch {
      break;
    }
  }
  return behaviorNote.trim();
}

export function mapProgressLikeToDisplayFields(src: ProgramProgressLikeRow | null): {
  progressUnit: string;
  progressUnitNote: string;
  progressLearning: string;
  progressLearningNote: string;
  progressPage: string;
  progressAssistantPage: string;
  progressCurrentPeriod: string;
  progressTotalPeriod: string;
  progressMaterial: string;
  progressCross: string;
  progressCrossNote: string;
  progressMemoPrimary: string;
  progressMemoNote: string;
  progressEval: string;
  progressEvalCode: string;
  progressEvalNote: string;
  progressClassRecord: string;
  progressClassRecordKind: 'empty' | 'lesson' | 'eval_log' | 'custom';
  progressClassRecordNote: string;
  progressBehaviorNote: string;
  progressRecordMemberGroupId: string | null;
  progressBgByField: ProgramProgressBgColorByField;
} {
  if (!src) {
    return {
      progressUnit: '',
      progressUnitNote: '',
      progressLearning: '',
      progressLearningNote: '',
      progressPage: '',
      progressAssistantPage: '',
      progressCurrentPeriod: '',
      progressTotalPeriod: '',
      progressMaterial: '',
      progressCross: '',
      progressCrossNote: '',
      progressMemoPrimary: '',
      progressMemoNote: '',
      progressEval: '',
      progressEvalCode: '',
      progressEvalNote: '',
      progressClassRecord: '',
      progressClassRecordKind: 'empty',
      progressClassRecordNote: '',
      progressBehaviorNote: '',
      progressRecordMemberGroupId: null,
      progressBgByField: {},
    };
  }
  const unitUn = getUnitTextAndNoteForDisplay(src.unit_text ?? '');
  const learnP = parseProgramProgressMemoJsonForDisplay(src.learning_text ?? '');
  const crossUn = getCrossTopicAndNoteForDisplay(src.record_cross_curricular_text ?? '');
  const memoParsed = parseProgramProgressMemoJsonForDisplay(src.record_memo_text ?? '');
  const evalP = parseMemoFieldStorageDeep(src.record_evaluation_text ?? '');
  const classP = parseMemoFieldStorageDeep(src.record_class_text ?? '');
  return {
    progressUnit: formatLearningTextForDisplay(unitUn.text),
    progressUnitNote: unitUn.note.trim(),
    progressLearning: formatLearningTextForDisplay(learnP.text),
    progressLearningNote: learnP.note.trim(),
    progressPage: (src.page_text ?? '').trim(),
    progressAssistantPage: (src.assistant_page_text ?? '').trim(),
    progressCurrentPeriod: (src.current_period_text ?? '').trim(),
    progressTotalPeriod: (src.total_period_text ?? '').trim(),
    progressMaterial: (src.material_text ?? '').trim(),
    progressCross: (crossUn.text ?? '').trim(),
    progressCrossNote: crossUn.note.trim(),
    progressMemoPrimary: memoParsed.text,
    progressMemoNote: memoParsed.note.trim(),
    progressEval: formatEvaluationCodeForDisplay(src.record_evaluation_text ?? ''),
    progressEvalCode: evalP.text.trim(),
    progressEvalNote: composeEvaluationMemoDisplayNote(evalP.note, {
      evaluationPlanAppliedId: evalP.evaluationPlanAppliedId,
      evaluationPlanElement: evalP.evaluationPlanElement,
      evaluationPlanMethod: evalP.evaluationPlanMethod,
      evaluationPlanSummary: evalP.evaluationPlanSummary,
    }),
    progressClassRecord: formatClassRecordCodeForDisplay(src.record_class_text ?? ''),
    progressClassRecordKind: classRecordKindFromRaw(src.record_class_text ?? ''),
    progressClassRecordNote: classP.note.trim(),
    progressBehaviorNote: parseClassRecordBehaviorNote(src.record_class_text ?? ''),
    progressRecordMemberGroupId: src.record_member_group_id ?? null,
    progressBgByField: src.bg_color_by_field ?? {},
  };
}

export function programProgressCellToProgressLike(c: ProgramProgressCell): ProgramProgressLikeRow {
  return {
    unit_text: c.unit_text,
    /** 본문 컬럼 + note/ref 컬럼을 편집기 JSON으로 — 인디케이터·표시가 동일 소스를 씀 */
    learning_text: cellLearningTextForEditor(c),
    page_text: c.page_text,
    assistant_page_text: c.assistant_page_text,
    current_period_text: c.current_period_text,
    total_period_text: c.total_period_text,
    material_text: c.material_text,
    record_cross_curricular_text: c.record_cross_curricular_text,
    record_memo_text: c.record_memo_text,
    record_evaluation_text: c.record_evaluation_text,
    record_class_text: c.record_class_text,
    record_member_group_id: c.record_member_group_id ?? null,
    bg_color_by_field: c.bg_color_by_field ?? {},
  };
}
